import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { User, ShoppingBag, Heart, Globe, Save, Loader2, ArrowRight, Sparkles, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function UserProfilePage() {
  const { language, setLang } = useLanguage();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const id = language === 'id';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    axios.get(`${API}/user/profile`, { withCredentials: true })
      .then(r => {
        setProfile(r.data);
        setForm({ name: r.data.name || '', phone: r.data.phone || '' });
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/user/profile`, { ...form, language }, { withCredentials: true });
      setProfile(prev => ({ ...prev, ...res.data }));
      setUser(res.data);
      toast.success(id ? 'Profil diperbarui' : 'Profile updated');
    } catch { toast.error(id ? 'Gagal menyimpan' : 'Failed to save'); }
    setSaving(false);
  };

  const fmt = (p) => new Intl.NumberFormat('id-ID').format(p);

  if (loading) {
    return (
      <ImmersiveLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-seikat-pink" size={24} />
        </div>
      </ImmersiveLayout>
    );
  }

  const stats = profile?.stats || {};

  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-32" data-testid="user-profile-page">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{id ? 'Akunmu' : 'Your Account'}</span>
            </div>
            <h1 className="mt-4 font-heading text-5xl lg:text-6xl text-seikat-bone leading-[0.95]">
              {id ? <>Halo, <em>{profile?.name?.split(' ')[0] || 'kekasih bunga'}</em>.</> : <>Hello, <em>{profile?.name?.split(' ')[0] || 'flower friend'}</em>.</>}
            </h1>
            <p className="mt-4 font-body text-seikat-bone/65 max-w-xl">
              {id ? 'Kelola identitas, bahasa, dan jejak rangkaianmu di satu tempat tenang.' : 'Manage identity, language, and your weaving journey in one calm place.'}
            </p>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-14">
            {[
              { label: id ? 'Pesanan' : 'Orders', value: stats.order_count || 0, icon: ShoppingBag },
              { label: id ? 'Memori' : 'Memories', value: stats.memory_count || 0, icon: Heart },
              { label: id ? 'Belanja' : 'Spent', value: `Rp ${fmt(stats.total_spent || 0)}`, icon: Sparkles }
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
                className="glass-dark rounded-2xl p-6"
                data-testid={`profile-stat-${i}`}
              >
                <s.icon size={16} className="text-seikat-pink mb-3" strokeWidth={1.4} />
                <div className="font-heading text-3xl text-seikat-bone">{s.value}</div>
                <div className="font-body text-[10px] tracking-widest uppercase text-seikat-bone-muted mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-10">
            {/* Form */}
            <div className="glass-dark rounded-2xl p-7">
              <header className="flex items-center gap-3 mb-6">
                <User size={16} className="text-seikat-lime" />
                <h2 className="font-heading text-2xl text-seikat-bone">{id ? 'Informasi Pribadi' : 'Personal Information'}</h2>
              </header>
              <div className="space-y-4">
                <Field label="Email">
                  <input value={profile?.email || ''} disabled className="immersive-input opacity-60" data-testid="profile-email" />
                </Field>
                <Field label={id ? 'Nama' : 'Name'}>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="immersive-input" data-testid="profile-name" />
                </Field>
                <Field label={id ? 'Telepon' : 'Phone'}>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+62..." className="immersive-input" data-testid="profile-phone" />
                </Field>
                <button onClick={handleSave} disabled={saving} className="btn-pill btn-pill--solid w-full justify-center mt-2" data-testid="save-profile-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {id ? 'Simpan Profil' : 'Save Profile'}
                </button>
              </div>
            </div>

            {/* Quick links */}
            <div className="glass-dark rounded-2xl p-7">
              <header className="flex items-center gap-3 mb-6">
                <Sparkles size={16} className="text-seikat-pink" />
                <h2 className="font-heading text-2xl text-seikat-bone">{id ? 'Menu Cepat' : 'Quick Links'}</h2>
              </header>
              <div className="space-y-2">
                {[
                  { to: '/orders', label: id ? 'Pesanan Saya' : 'My Orders', icon: ShoppingBag },
                  { to: '/memories', label: id ? 'Memori Saya' : 'My Memories', icon: Heart },
                  { to: '/wishlist', label: id ? 'Wishlist' : 'Wishlist', icon: Heart },
                  { to: '/loyalty', label: id ? 'Poin & Referral' : 'Loyalty & Referral', icon: ShoppingBag },
                  { to: '/verify-email', label: id ? 'Verifikasi Email' : 'Verify Email', icon: Heart },
                  { to: '/memory-bouquet', label: id ? 'Mulai Memory Bouquet' : 'Begin Memory Bouquet', icon: Sparkles }
                ].map((link) => (
                  <Link key={link.to} to={link.to} className="flex items-center justify-between p-4 rounded-xl border border-white/8 hover:border-seikat-pink/40 hover:bg-seikat-pink/5 transition-all group">
                    <div className="flex items-center gap-3">
                      <link.icon size={15} className="text-seikat-pink" />
                      <span className="font-body text-sm text-seikat-bone">{link.label}</span>
                    </div>
                    <ArrowRight size={14} className="text-seikat-bone-muted group-hover:translate-x-1 group-hover:text-seikat-pink transition-all" />
                  </Link>
                ))}
              </div>

              {/* Language */}
              <div className="mt-7">
                <p className="font-body text-xs tracking-widest uppercase text-seikat-bone-muted mb-3 flex items-center gap-1.5">
                  <Globe size={11} /> {id ? 'Bahasa' : 'Language'}
                </p>
                <div className="flex gap-2">
                  {[{ code: 'id', label: 'Bahasa Indonesia' }, { code: 'en', label: 'English' }].map(l => (
                    <button
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      data-testid={`lang-${l.code}`}
                      className={`flex-1 py-2.5 rounded-full text-xs tracking-widest uppercase transition-all ${language === l.code ? 'bg-seikat-pink text-seikat-ink' : 'border border-white/10 text-seikat-bone-muted hover:text-seikat-bone'}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={async () => { await logout(); navigate('/'); }}
                className="mt-8 w-full flex items-center justify-center gap-2 py-3 rounded-full text-xs tracking-[0.25em] uppercase text-seikat-bone-muted hover:text-seikat-pink transition-colors"
                data-testid="profile-logout"
              >
                <LogOut size={12} /> {id ? 'Keluar' : 'Sign Out'}
              </button>
            </div>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 font-body text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">{label}</div>
      {children}
    </label>
  );
}
